import { Vector2, Vector3 } from 'three';

import {
  Conic,
  getConicDirection,
  getConicParameter,
  getConicPoint,
  intersectConicWithLine2d,
  tessellateConic,
} from '../../elements/design/design-node/utils/conic';
import { AssetLine } from './asset-line';

/**
 * A line from start to end, straight or along a curve, that crossings are found between.
 */
export type CrossingLine = { start: Vector3; end: Vector3; conic?: Conic };

export const toCrossingLine = (line: AssetLine): CrossingLine =>
  'conic' in line ? { start: line.conic.start, end: line.conic.end, conic: line.conic } : line;

/**
 * How far outside of a line (relative to its length) a crossing may be, so that lines that meet at
 * an end cross there despite rounding.
 */
const endTolerance = 1e-9;

/**
 * The number of straight pieces a curve is split into to find where it crosses another curve,
 * before the crossing is moved onto both curves.
 */
const curvePieces = 32;

const refineSteps = 8;

const cross2d = (a: Vector2, b: Vector2) => a.x * b.y - a.y * b.x;

const toVector2 = (p: Vector3) => new Vector2(p.x, p.y);

/**
 * Gets where on the straight line from start to end (0 to 1) the point is, in the horizontal plane.
 */
function getLineParameter(start: Vector3, end: Vector3, point: Vector3): number {
  const direction = toVector2(end).sub(toVector2(start));
  return toVector2(point).sub(toVector2(start)).dot(direction) / direction.lengthSq();
}

const isOnLine = (t: number) => t >= -endTolerance && t <= 1 + endTolerance;

/**
 * Gets the crossings of two straight lines, at the height of the first line.
 */
function crossStraightLines(a: CrossingLine, b: CrossingLine): Vector3[] {
  const r = toVector2(a.end).sub(toVector2(a.start));
  const s = toVector2(b.end).sub(toVector2(b.start));
  const denominator = cross2d(r, s);

  // Parallel, or a line without length.
  if (Math.abs(denominator) <= 1e-12 * r.length() * s.length()) {
    return [];
  }

  const offset = toVector2(b.start).sub(toVector2(a.start));
  const t = cross2d(offset, s) / denominator;
  const u = cross2d(offset, r) / denominator;

  return isOnLine(t) && isOnLine(u) ? [a.start.clone().lerp(a.end, t)] : [];
}

/**
 * Gets the crossings of a curve with a straight line, at the height of the curve.
 */
function crossCurveWithStraightLine(conic: Conic, line: CrossingLine): Vector3[] {
  const direction = toVector2(line.end).sub(toVector2(line.start));

  if (direction.lengthSq() === 0) {
    return [];
  }

  return intersectConicWithLine2d(conic, line.start, direction)
    .map((t) => getConicPoint(conic, t))
    .filter((p) => isOnLine(getLineParameter(line.start, line.end, p)));
}

/**
 * Moves a crossing of the curves at t on the first curve closer to where they cross, by crossing
 * the first curve with the tangent of the second where it is closest. Undefined if the curves
 * don't cross there.
 */
function refineCurveCrossing(a: Conic, b: Conic, t: number): Vector3 | undefined {
  let point = getConicPoint(a, t);

  for (let i = 0; i < refineSteps; i++) {
    const s = getConicParameter(b, point);
    const onB = getConicPoint(b, s);
    const crossings = intersectConicWithLine2d(a, onB, getConicDirection(b, s));

    if (crossings.length === 0) {
      return undefined;
    }

    t = crossings.reduce((closest, x) => (Math.abs(x - t) < Math.abs(closest - t) ? x : closest));
    point = getConicPoint(a, t);
  }

  // The curves cross, rather than only come close, if the point is on both of them.
  const onB = getConicPoint(b, getConicParameter(b, point));
  const size = a.start.distanceTo(a.end) + b.start.distanceTo(b.end);

  return toVector2(onB).distanceTo(toVector2(point)) <= 1e-6 * size ? point : undefined;
}

/**
 * Gets the crossings of two curves, at the height of the first curve.
 */
function crossCurves(a: Conic, b: Conic): Vector3[] {
  const pieces = tessellateConic(b, curvePieces);
  const crossings: Vector3[] = [];

  for (let i = 1; i < pieces.length; i++) {
    const piece = { start: pieces[i - 1], end: pieces[i] };

    for (const approximate of crossCurveWithStraightLine(a, piece)) {
      const point = refineCurveCrossing(a, b, getConicParameter(a, approximate));

      // A crossing at the end of a piece is found by both pieces.
      if (point && !crossings.some((x) => x.distanceTo(point) <= 1e-9 * (1 + point.length()))) {
        crossings.push(point);
      }
    }
  }

  return crossings;
}

/**
 * Gets the points where two lines cross in the horizontal plane, at the height of the first line.
 * Lines that meet at an end cross there. Parallel lines don't cross.
 */
export function getLineCrossings(a: CrossingLine, b: CrossingLine): Vector3[] {
  if (a.conic && b.conic) {
    return crossCurves(a.conic, b.conic);
  }

  if (a.conic) {
    return crossCurveWithStraightLine(a.conic, b);
  }

  if (b.conic) {
    // At the height of the straight line.
    return crossCurveWithStraightLine(b.conic, a).map((p) =>
      a.start
        .clone()
        .lerp(a.end, getLineParameter(a.start, a.end, p))
        .setX(p.x)
        .setY(p.y),
    );
  }

  return crossStraightLines(a, b);
}

/**
 * Gets the height of the line where it passes the point in the horizontal plane.
 */
export function getHeightOnLine(line: CrossingLine, point: Vector3): number {
  if (line.conic) {
    return getConicPoint(line.conic, getConicParameter(line.conic, point)).z;
  }

  const t = getLineParameter(line.start, line.end, point);
  return line.start.z + (line.end.z - line.start.z) * (Number.isFinite(t) ? t : 0);
}

/**
 * Whether the point is at the start or end of the line in the horizontal plane, relative to its
 * length.
 */
function isAtEnd(line: CrossingLine, point: Vector3): boolean {
  const tolerance = 1e-6 * Math.max(toVector2(line.start).distanceTo(toVector2(line.end)), 1e-6);
  const p = toVector2(point);

  return (
    p.distanceTo(toVector2(line.start)) <= tolerance ||
    p.distanceTo(toVector2(line.end)) <= tolerance
  );
}

/**
 * Gets the crossing of the lines that is closest to the cursor on screen, within the distance
 * (pixels). The lines are in the order of how close they are to the cursor, and the crossing is at
 * the height of the closer line.
 *
 * Where both lines end, they only meet, and don't intersect: the vertices of a polyline, the
 * corners of a square, the pieces of a circle. Where one line ends on the other (a T), they do.
 */
export function findClosestCrossing<T extends CrossingLine>(
  lines: T[],
  toScreen: (point: Vector3) => Vector2,
  cursor: Vector2,
  maxDistance: number,
): { point: Vector3; lines: [T, T] } | undefined {
  let closest: { point: Vector3; lines: [T, T] } | undefined;
  let closestDistance = maxDistance;

  for (let i = 0; i < lines.length; i++) {
    for (let j = i + 1; j < lines.length; j++) {
      for (const point of getLineCrossings(lines[i], lines[j])) {
        if (isAtEnd(lines[i], point) && isAtEnd(lines[j], point)) {
          continue;
        }

        const distance = toScreen(point).distanceTo(cursor);

        if (distance <= closestDistance) {
          closestDistance = distance;
          closest = { point, lines: [lines[i], lines[j]] };
        }
      }
    }
  }

  return closest;
}
